import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { CampaignStatuses } from 'src/store/constants';
import {
  useAssetsControllerGetDistinctCampaignOptionsQuery,
} from 'src/store/leela-api';

export const COUNTRY_OPTIONS = [
  'AE', 'AR', 'AU', 'BE', 'BO', 'BR', 'CA', 'CH', 'CL', 'CO',
  'CR', 'CZ', 'DE', 'DK', 'DO', 'EC', 'EG', 'ES', 'FI', 'FR',
  'GB', 'GT', 'HN', 'HU', 'ID', 'IE', 'IN', 'IT', 'JP', 'MX',
  'MY', 'NG', 'NI', 'NL', 'NO', 'NZ', 'PA', 'PE', 'PH', 'PL',
  'PR', 'PT', 'PY', 'RO', 'SA', 'SE', 'SG', 'SV', 'TH', 'TR',
  'US', 'UY', 'VE', 'ZA',
] as const;

export interface CampaignFilterState {
  publisher: string;
  status: string;
  countries: string[];
  businessLine: string;
}

export interface CampaignFilterBarProps {
  filters: CampaignFilterState;
  onFiltersChange: (filters: CampaignFilterState) => void;
  search: string;
  onSearchChange: (search: string) => void;
}

const STATUS_OPTIONS = [
  CampaignStatuses.ACTIVE,
  CampaignStatuses.PAUSED,
  CampaignStatuses.SUSPENDED,
  CampaignStatuses.ARCHIVED,
];

interface CampaignOption { productStrategies?: unknown[] }

const deriveBusinessLines = (campaigns: CampaignOption[] | undefined): string[] => {
  if (!campaigns) return [];
  const bls = new Set<string>();
  campaigns.forEach((campaign) => {
    ((campaign.productStrategies ?? []) as Array<{ businessLine?: string }>).forEach((strategy) => {
      if (strategy.businessLine) bls.add(strategy.businessLine);
    });
  });
  return Array.from(bls).sort();
};

export const CampaignFilterBar: React.FC<CampaignFilterBarProps> = ({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
}) => {
  const { t } = useTranslation();
  const [localSearch, setLocalSearch] = useState(search);
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => { onSearchChangeRef.current = onSearchChange; });

  // Sync local search when parent value changes (e.g. clear all)
  useEffect(() => { setLocalSearch(search); }, [search]);

  // Debounce: propagate local search to parent after 300ms of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) onSearchChangeRef.current(localSearch);
    }, 300);
    return () => { clearTimeout(timer); };
  }, [localSearch, search]);

  const { data: allCampaignOptions } = useAssetsControllerGetDistinctCampaignOptionsQuery();

  const publisherOptions = useMemo(() => {
    if (!allCampaignOptions) return [];
    const pubs = new Set<string>();
    allCampaignOptions.forEach((c) => { if (c.publisher) pubs.add(c.publisher); });
    return Array.from(pubs).sort();
  }, [allCampaignOptions]);

  const businessLineOptions = useMemo(
    () => deriveBusinessLines(allCampaignOptions),
    [allCampaignOptions],
  );

  const hasActiveFilters =
    filters.publisher !== '' || filters.status !== '' || search !== '' ||
    filters.countries.length > 0 || filters.businessLine !== '';

  const handleClearAll = useCallback(() => {
    setLocalSearch('');
    onSearchChangeRef.current('');
    onFiltersChange({ publisher: '', status: '', countries: [], businessLine: '' });
  }, [onFiltersChange]);

  return (
    <Column spacing="200">
      <Text type="b300"><b>{t('filter_campaigns_header')}</b></Text>
      <Row alignmentVertical="bottom" spacing="200" wrap="down">
          <SearchField
            data-testid="campaign-name-search"
            label={t('campaign_name')}
            placeholder={t('search_campaigns')}
            size="small"
            value={localSearch}
            width={250}
            onChange={(value: string) => { setLocalSearch(value); }}
            onSubmit={() => { onSearchChangeRef.current(localSearch); }}
          />
          <Select
            data-testid="campaign-publisher-filter"
            label={t('publisher')}
            placeholder={t('all')}
            size="small"
            value={filters.publisher}
            width={200}
            onChange={(publisher: string) => { onFiltersChange({ ...filters, publisher }); }}
          >
            <SelectOption key="__all__" label={t('all')} value="" />
            {publisherOptions.map((pub) => (
              <SelectOption key={pub} label={pub} value={pub} />
            ))}
          </Select>
          <Select
            data-testid="campaign-status-filter"
            label={t('form_global_status')}
            placeholder={t('all')}
            size="small"
            value={filters.status}
            width={200}
            onChange={(status: string) => { onFiltersChange({ ...filters, status }); }}
          >
            <SelectOption key="__all__" label={t('all')} value="" />
            {STATUS_OPTIONS.map((s) => (
              <SelectOption key={s} label={s} value={s} />
            ))}
          </Select>
          <Select
            data-testid="campaign-country-filter"
            label={t('country')}
            placeholder={t('all')}
            size="small"
            value={filters.countries}
            width={200}
            onChange={(countries: string[]) => { onFiltersChange({ ...filters, countries }); }}
          >
            {COUNTRY_OPTIONS.map((c) => (
              <SelectOption key={c} label={c} value={c} />
            ))}
          </Select>
          <Select
            data-testid="campaign-businessline-filter"
            label={t('business_line')}
            placeholder={t('all')}
            size="small"
            value={filters.businessLine}
            width={200}
            onChange={(businessLine: string) => { onFiltersChange({ ...filters, businessLine }); }}
          >
            <SelectOption key="__all__" label={t('all')} value="" />
            {businessLineOptions.map((bl) => (
              <SelectOption key={bl} label={bl} value={bl} />
            ))}
          </Select>
        </Row>

      {hasActiveFilters && (
        <Row alignmentVertical="center" spacing="200" wrap="down">
          <Text color="secondary" type="b200">{t('active_filters')}</Text>
          {filters.publisher && (
            <Tag data-testid="filter-tag-publisher" type="theme"
              onClose={() => { onFiltersChange({ ...filters, publisher: '' }); }}>
              {`${t('publisher')}: ${filters.publisher}`}
            </Tag>
          )}
          {filters.status && (
            <Tag data-testid="filter-tag-status" type="theme"
              onClose={() => { onFiltersChange({ ...filters, status: '' }); }}>
              {`${t('form_global_status')}: ${filters.status}`}
            </Tag>
          )}
          {search && (
            <Tag data-testid="filter-tag-campaign-search" type="theme"
              onClose={() => { setLocalSearch(''); onSearchChangeRef.current(''); }}>
              {`${t('campaign_name')}: "${search}"`}
            </Tag>
          )}
          {filters.countries.map((c) => (
            <Tag data-testid={`filter-tag-country-${c}`} key={`country-${c}`} type="theme"
              onClose={() => { onFiltersChange({ ...filters, countries: filters.countries.filter((cc) => cc !== c) }); }}>
              {`${t('country')}: ${c}`}
            </Tag>
          ))}
          {filters.businessLine && (
            <Tag data-testid="filter-tag-businessline" type="theme"
              onClose={() => { onFiltersChange({ ...filters, businessLine: '' }); }}>
              {`${t('business_line')}: ${filters.businessLine}`}
            </Tag>
          )}
          <Button data-testid="campaign-clear-all-filters" size="small" type="link" onClick={handleClearAll}>
            {t('clear_filters')}
          </Button>
        </Row>
      )}
    </Column>
  );
};
